import React, { useEffect, useState } from "react";
import { sortBy } from "lodash";
import moment from "moment";
import { Formik } from "formik";
import Button from "../Button/Button";
import Loader from "../Loader/Loader";
import FormFormat from "../FormFormat/FormFormat";
import {
  executeActivityApi,
  uploadActivityApi,
  formSubmitApi,
  removeActivityApi,
  setupAxiosDefaults,
  getGraphXmlApi,
} from "../../apiService";
import useGetForm from "../../customHooks/useGetForm";
import { useFormValidationSchema } from "../../customHooks/useFormValidationSchema";
import {
  APPLICATION_ID,
  mergeFieldsIntoForm,
  formatMoney,
  unformatMoney,
  htmlEscape,
  HTML_ESCAPE_TYPES,
  isSubProcess,
  formatFloat,
  unformatFloat,
  getFilePath,
  downloadRemoteFile,
  isHttpUrl,
  isJavaScriptFunction,
  formserverBaseURL,
  setFormserverBaseURL,
} from "../../utilities";
import useGetGraph from "../../customHooks/useGetGraph";
import useErrorMessage from "../../customHooks/useErrorMessage";
import logger from "../../logger";
import "./activity-form.scss";
import { useStateValue } from "../../state/State";
import { actions } from "../../state/ErrorReducer";
import FileUploadStopChar from '../../FileUploadStopChar.txt';
import Modal from '../Modal/Modal'
import pdfIcon from '../../content/images/pdf-icon.svg'

const ActivityForm = (props) => {
  const {
    onSubmit = () => {},
    onUpdate = () => {},
    onCancel = () => {},
    uploadServiceUrl = null,
    uploadFileURL = null,
    removeFileURL = null,
    serverUrl = null
  } = props || {};  

  let selectedElement = null;
  const [, dispatch] = useStateValue();
  const [form, setForm] = useState({
    id: "",
    labellocation: "",
    phases: [],
    error: null,
    fetching: false,
    accepting: false,
    submitting: false
  });
  const [focusedElem, setFocusedElem] = useState(null);
  const [fileUploadStopChars, setFileUploadStopChars] = useState("");
  const [formEvents, setFormEvents] = useState([]);
  const { formserverConfigs } = window;
  const { graphId, data, filePath } = useGetGraph();

  const [showSubmitSuccessPopup, setSubmitSuccessPopup] = useState(false);
  const [submitSuccessText, setSubmitSuccessText] = useState(null);

  const {
    form: formserver,
    formEvents: formserverEvents,
    graphJSON,
    subscription,
  } = useGetForm({
    data,
    form,
    formEvents,
    graphId,
    filePath,
    setForm,
    setFormEvents,
  });

  const displayUserFriendlyErrorMessage = useErrorMessage();

  const validationSchema = useFormValidationSchema(formserver, formserverEvents);

  const executeRoboticEvents = () => {
    const isExistRoboticEvents = formserver.phases
      .map((p) => p.elements.some((e) => e.pending && e.readOnly))
      .some((e) => e);
    if (isExistRoboticEvents) {
      const element = formserver.phases[0].elements.find(e => !e.readOnly && !e.presentationType === 'label');
      if (element) {
        handleActivityBlur(element.value, element, [], () => {}, () => {});
      }
    }
  };

  useEffect(() => {
    if (formserver && !formserver.fetching && formserver.phases && formserver.phases.length > 0) {
      executeRoboticEvents();
    }
  }, [formserverEvents]);

  const getFileUploadStopChars = () => {
    fetch(formserverBaseURL + FileUploadStopChar)
      .then((response) => response.text())
      .then((data) => setFileUploadStopChars(data));
  };

  useEffect(() => {
    setFormserverBaseURL(serverUrl || (window?.formserverConfigs?.serverUrl || ""));
    setupAxiosDefaults(serverUrl || (window?.formserverConfigs?.serverUrl || ""));
  }, []);

  useEffect(() => {
    if (formserverBaseURL && formserverBaseURL !== "/") {
      getFileUploadStopChars();
    }
  }, [formserverBaseURL]);

  useEffect(() => {
    // https://stackoverflow.com/questions/55535022/how-to-prevent-user-from-going-back-in-react-router-dom/64494677#64494677
    // Pushing current state to history to prevent going back when submit success popup is opened
    if (showSubmitSuccessPopup) {
      window.history.pushState(null, document.title, window.location.href);
      window.onpopstate = () => {
        window.history.pushState(null, document.title, window.location.href);
      };
    } else if (window.onpopstate) {
      window.onpopstate = null;
      window.history.go(-1);
    }
  }, [showSubmitSuccessPopup]);

  const validateFilename = (fileName) => {
    const invalidCharsRegex = new RegExp(`.*[${fileUploadStopChars.replaceAll(",", "")}]+.*`);
    if (fileName && invalidCharsRegex.test(fileName)) {
      dispatch({
        type: actions.SET_ERROR,
        error: {
          error: { message: "Oops - an error occured" },
          errorInfo: { componentStack: 'File name contains invalid characters' },
        },
      });
      return false;
    }
    return true;
  };

  const getVisibleActivitiesWithPhases = () => {
    const {formating: graphType, phases} = formserver || {};
    const phasesWithVisibleActivities = phases.map((phase) => {
      return {
        ...phase,
        elements: sortBy(
          phase.elements.filter(
            ({ type, visible }) =>
              (type === "event" || isSubProcess(type)) && visible
          ),
          "seq"
        ),
      };
    });

    if (graphType !== "Phases") {
      return phasesWithVisibleActivities.filter((phase) => phase.elements.length);
    }

    return phasesWithVisibleActivities;
  };

  const handlePhaseToggle = (currentPhase) => {
    setForm({
      ...formserver,
      phases: formserver.phases.map((phase, index) => ({
        ...phase,
        isActive: currentPhase.isActive === undefined && index === 0 
          ? false
          : currentPhase.isActive
            ? !currentPhase.isActive
            : phase.phaseNo === currentPhase.phaseNo,
      })),
    });
  };

  const handleActivityChange = (event, element) => {
    const { value, files } = event.target;
    setForm({
      ...formserver,
      phases: formserver.phases.map((phase) => {
        const elements = updateSelectedElement(
          phase.elements,
          element,
          value,
          files
        );
        return { ...phase, elements };
      }),
    });
  };

  const getExecutedValue = (value, presentationType) => {
    if (presentationType === "date" && value !== "") {
      var currentOffset = moment().utcOffset();
      var dateVal = new moment(value).set({
        hour: 9,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      return dateVal.add(currentOffset, "m").toISOString();
    }
    if (presentationType === "money") {
      return unformatMoney(value).toString();
    }
    if (presentationType === "float") {
      return unformatFloat(value, navigator.language).toString();
    }
    if (value && value.length > 0 && presentationType === "textbox") {
      // Check if the value is valid JSON
        let isJson = false;
        try {
            JSON.parse(value);  // If this succeeds, it's JSON
            isJson = true;
        } catch (e) {
            isJson = false;
        }

        // If the value is not JSON, replace newline characters with the XML entity
        if (!isJson) {
            return value.toString().replace(new RegExp("\n", "g"), "&#xA;");
        }

        // Return the value as is if it's JSON
        return value;
    }
    if (value && HTML_ESCAPE_TYPES[presentationType]) {
      return htmlEscape(value);
    }
    if (!value || value === "false") return null;
    return value;
  };

  const getSelectedElements = (elements, element) => {
    elements.forEach((elem) => {
      if (isSubProcess(elem.type)) {
        getSelectedElements(elem.elements, element);
      }
      if (elem.activityId === element.activityId) {
        selectedElement = elem;
      }
    });
  };

  const updateSelectedElement = (elements, element, value, files) => {
    return elements.map((elem) => {
      if (isSubProcess(elem.type)) {
        updateSelectedElement(elem.elements, element, value, files);
      }
      if (elem.activityId === element.activityId) {
        if (
          (elem.presentationType === "file" ||
            elem.presentationType === "image") &&
          files
        ) {
          const fileName = files && files[0] && files[0].name ? files[0].name : "";
          if (validateFilename(fileName)) {
            Object.assign(elem, {
              value: fileName,
              previousValue: elem.value,
              files,
            });
          }
        } else {
          Object.assign(elem, {
            previousValue: elem.value,
            value,
          });
        }
      }
      return elem;
    });
  };

  const isValidate = (value, element, errors) => {
    const filterPhases = formserver.phases
      .map((phase) => {
        getSelectedElements(phase.elements, element, value);
        return {
          ...phase,
          elements: selectedElement ? [selectedElement] : [],
        };
      })
      .find((phase) => phase.elements.length);

    const isValueEmpty =
      filterPhases.elements[0].previousValue === undefined && value === "";
    const isValueSame = !element.pending && filterPhases.elements[0].previousValue === value;
    const isValueInvalid =
      (isValueEmpty && errors[element.activityId]) ||
      errors[element.activityId];

    return !(
      !['button', 'checkbox', 'choice', 'date', 'datetime'].includes(element.presentationType) &&
      (isValueEmpty || isValueSame || isValueInvalid)
    );
  };

  const formikValidate = (values) => {
    if (values.files) {
      const validation = values.element.validations.find((item) => {
        const { params, ruleType } = item;
        const file = values.files[0];
        if (
          ruleType === "extension" &&
          !params[0].split(",").includes(file.name.split(".").pop())
        )
          return item;

        if (
          ruleType === "fileSize" &&
          file.size / (1024 * 1024) > parseInt(params[0])
        )
          return item;
      });

      if (validation) {
        return { [values.element.activityId]: validation.params[1] };
      }
    }
    return {};
  };

  const handleActivityPreBlur = (
    e,
    element,
    errors,
    setErrors,
    setValues,
    handleBlur
  ) => {
    let value = e;
    if (e.target) {
      handleBlur(e);
      if (e.target.type === "checkbox") {
        value = e.target.checked.toString();
      } else {
        value = e.target.value;
      }
    }

    if (isValidate(value, element, errors)) {
      handleActivityBlur(value, element, errors, setErrors, setValues);
    }
  };

  const handleActivityBlur = (value, element, errors, setErrors, setValues) => {
    setForm({
      ...formserver,
      phases: formserver.phases.map((phase) => {
        const elements = phase.elements.map((elem) => {
          if (elem.activityId === element.activityId) {
            if (elem.presentationType === "file" && element.files) {
              return {
                ...elem,
                file: element.files[0],
              };
            }
            if (elem.presentationType === "money") {
              return { ...elem, value: formatMoney(value) };
            }

            if (elem.presentationType === "float") {
              return {
                ...elem,
                value: formatFloat(unformatFloat(value), navigator.language),
              };
            }
            return { ...elem, value };
          }
          return elem;
        });
        return { ...phase, elements };
      }),
    });

    const executedValue = getExecutedValue(value, element.presentationType);
    // TODO: We need to pass in userid and role dynamically
    return handleExecuteActivity(
      {
        formid: formserver.id,
        activityid: element.id,
        value: executedValue,
        isNull: executedValue ? "false" : "true",
        userid: "",
        role: "",
      },
      errors,
      setErrors,
      setValues
    );
  };

  const handleSetFocus = () => {
    // ".public-DraftEditor-content" is for html editor
    const dataTypes = ["input", "select", "textarea", "button", ".public-DraftEditor-content"];
    const form = document.querySelectorAll(".dcrform")[0];
    if (form) {
      const elements = form.querySelectorAll(dataTypes);
      for (let i = 0; i < elements.length; i++) {
        if (elements[i] === focusedElem) {
          const focus_input = i + 1;
          if (elements[focus_input]) {
            const elem = elements[focus_input];
            elem.focus();
          }
        }
      }
    }
  };

  const handleOnKeyDown = (evt, element, errors, setErrors, setValues) => {
    const { value } = evt.target;
    const tabKey = 9;
    setFocusedElem(document.activeElement);
    if (evt.keyCode === tabKey && isValidate(value, element, errors)) {
      evt.preventDefault();
      handleActivityBlur(value, element, errors, setErrors, setValues).then(
        () => {
          handleSetFocus();
        }
      );
    }
  };

  const handleExecuteActivity = (element, errors, setErrors, setValues) => {
    return executeActivityApi(element)
      .then((response) => {
        const { events, accepting, phaseNo } = response.data;
        const notRequiredEvents = events && events.length > 0 && events
          .filter(({ pending }) => !pending)
          .map(({ id }) => ({ [id]: "" }));
        const transformIntoObject = notRequiredEvents && notRequiredEvents.length > 0 && notRequiredEvents.reduce(
          (acc, cur) => ({
            ...acc,
            [Object.keys(cur)]: Object.values(cur).toString(),
          }),
          {}
        );
        setErrors({ ...errors, ...transformIntoObject });
        const newForm = mergeFieldsIntoForm({
          form: formserver,
          events,
          accepting,
          phaseNo,
          graphLocalizedJSON: graphJSON,
        });
        setForm(newForm);
        setValues(newForm);
        setFormEvents(events);
        if (formserverConfigs && formserverConfigs.onUpdate) {
          // will be add actual xml after discussion
          formserverConfigs.onUpdate("<update>for testing purpose</update>");
        } else if (onUpdate) {
          onUpdate("<update>for testing purpose</update>");
        }
      })
      .catch((error) => {
        displayUserFriendlyErrorMessage(error);
        console.log("handleExecuteActivity err......", error);
        logger.error({
          message: error,
          module: "Formserver",
          fileName: "ActivityForm.js",
          functionName: "executeActivityApi",
          logLevel: "Critical",
          details: error,
          parameters: JSON.stringify(element),
          url: window.location.href,
          application: APPLICATION_ID, //TODO: for formserver we need application id
        });
      });
  };

  const handleUploadActivityFile = (
    files,
    element,
    errors,
    setErrors,
    setValues
  ) => {
    const data = new FormData();
    data.append(element.id, files[0]);
    data.append("fileInformation", files[0].name);
    data.append("token", formserver.id);
    data.append("id", formserver.id);
    data.append("eventId", element.id);
    const url = uploadFileURL || (window?.formserverConfigs?.uploadFileURL || "");
    const fileUploadServiceUrl = uploadServiceUrl || (window?.formserverConfigs?.uploadServiceUrl || "");
    return uploadActivityApi(url, data)
      .then((response) => {
        // TODO: We need to pass in userid and role dynamically
        handleExecuteActivity(
          {
            formid: formserver.id,
            activityid: element.id,
            value: getFilePath(fileUploadServiceUrl, formserver.id, files[0].name),
            userid: "",
            role: "",
          },
          errors,
          setErrors,
          setValues
        );
        return response;
      })
      .catch((error) => {
        displayUserFriendlyErrorMessage(error);
        logger.error({
          message: error,
          module: "Formserver",
          fileName: "ActivityForm.js",
          functionName: "uploadActivityApi",
          logLevel: "Critical",
          details: error,
          parameters: JSON.stringify({ url, data }),
          url: window.location.href,
          application: APPLICATION_ID, //TODO: for formserver we need application id
        });
      });
  };

  const handleRemoveActivityFile = (
    files,
    element,
    errors,
    setErrors,
    setValues
  ) => {
    const data = new URLSearchParams();
    data.append("fileInformation", files[0].name);
    data.append("token", formserver.id);
    data.append("id", formserver.id);
    data.append("fileNames", files[0].name);
    const url = removeFileURL || (window?.formserverConfigs?.removeFileURL || "");
    return removeActivityApi(url, data)
      .then((_) => {
        // TODO: We need to pass in userid and role dynamically
        handleExecuteActivity(
          {
            formid: formserver.id,
            activityid: element.id,
            value: "",
            userid: "",
            role: "",
          },
          errors,
          setErrors,
          setValues
        );
      })
      .catch((error) => {
        displayUserFriendlyErrorMessage(error);
        logger.error({
          message: error,
          module: "Formserver",
          fileName: "ActivityForm.js",
          functionName: "removeActivityApi",
          logLevel: "Critical",
          details: error,
          parameters: JSON.stringify({ url, data }),
          url: window.location.href,
          application: APPLICATION_ID, //TODO: for formserver we need application id
        });
      });
  };

  const handleCancel = () => {
    if (subscription?.cancelCallback) {
      handleCallback(subscription.cancelCallback);
    }
    if (formserverConfigs && formserverConfigs.onCancel) {
      formserverConfigs.onCancel();
    } else if (onCancel) {
      onCancel();
    }
  };

  const handleFormSubmit = () => {
    setForm({ ...form, submitting: true });
    getGraphXmlApi({ id: formserver.id })
      .then(({ data: graphXml }) => {
        formSubmitApi({ formid: formserver.id })
          .then(({ data: logXml }) => {
            if (formserverConfigs && formserverConfigs.onSubmit) {
              formserverConfigs.onSubmit(logXml, graphXml);
            } else if (onSubmit) {
              onSubmit(logXml, graphXml);
            }
            if (subscription?.recipientCallback) {
              handleCallback(subscription.recipientCallback);
            }
            else if (subscription?.showPopupOnSubmit && subscription?.allowDownloadPDF) {
              setForm({ ...form, submitting: false });
              setSubmitSuccessText(subscription?.submitSuccessText);
              setSubmitSuccessPopup(true);
            } else {
              setForm({ ...form, submitting: false });
            }
          })
          .catch((error) => {
            setForm({ ...form, submitting: false });
            displayUserFriendlyErrorMessage(error);
            logger.error({
              message: error,
              module: "Formserver",
              fileName: "ActivityForm.js",
              functionName: "formSubmitApi",
              logLevel: "Critical",
              details: error,
              parameters: JSON.stringify({ formId: formserver.id }),
              url: window.location.href,
              application: APPLICATION_ID, //TODO: for formserver we need application id
            });
          })
      })
      .catch((error) => {
        setForm({ ...form, submitting: false });
        displayUserFriendlyErrorMessage(error);
        logger.error({
          message: error,
          module: "Formserver",
          fileName: "ActivityForm.js",
          functionName: "getGraphXmlApi",
          logLevel: "Critical",
          details: error,
          parameters: JSON.stringify({ formId: formserver.id }),
          url: window.location.href,
          application: APPLICATION_ID, //TODO: for formserver we need application id
        });
      });
  };

  const downloadFilledFormPDF = () => {
    const fileUploadServiceUrl = uploadServiceUrl || (window?.formserverConfigs?.uploadServiceUrl || '');
    const filledFormPDFPath = getFilePath(fileUploadServiceUrl, formserver.id, 'FilledForm.pdf');
    downloadRemoteFile(filledFormPDFPath, subscription?.pdfFileName ?? 'FilledForm.pdf');
  };

  const handleCallback = (callback) => {
    if (isHttpUrl(callback)) { //url for redirection
      window.open(callback, "_self");
    } else if (isJavaScriptFunction(callback)) { //javascript function name
      window[callback]();
    }
  };

  if (formserver.error) throw new Error(formserver.error);

  if (formserver.fetching) {
    return <Loader />;
  }

  return (
    <>
      {formserver.submitting && <Loader fullscreen />}
      {showSubmitSuccessPopup && (
        <Modal
          canClose={false}
          message={submitSuccessText}
          information={
            <div
              onClick={downloadFilledFormPDF}
              className="activity-form__downloadbtn"
            >
              <img
                alt="Pdf Icon"
                className="activity-form__pdf-icon"
                src={formserverBaseURL + pdfIcon}
              />
              Download
            </div>
          }
        />
      )}
      <Formik
        initialValues={formserver}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validate={formikValidate}
        onSubmit={handleFormSubmit}
      >
        {(props) => {
          const {
            errors,
            values,
            touched,
            isValid,
            setErrors,
            setValues,
            handleChange,
            handleBlur,
            handleSubmit,
            validateForm,
          } = props;
          return (
            <div
              className={`activity-form activity-form--${values.labellocation ? values.labellocation.toLowerCase() : 'horizontal'}`}
            >
              {!values.fetching && values.phases.length > 0 && (
                <>
                  <FormFormat
                    type={values.formating}
                    accepting={values.accepting}
                    phaseNo={values.phaseNo}
                    formShowInitialPhase={values.formShowInitialPhase}
                    formShowPendingCount={values.formShowPendingCount}
                    sendtext={values.sendtext}
                    canceltext={values.canceltext}
                    isValid={isValid}
                    touched={touched}
                    errors={errors}
                    phases={getVisibleActivitiesWithPhases()}
                    onActivityChange={(e, element) => {
                      handleChange(e);
                      handleActivityChange(e, element);
                    }}
                    onActivityBlur={(e, element) =>
                      handleActivityPreBlur(
                        e,
                        element,
                        errors,
                        setErrors,
                        setValues,
                        handleBlur
                      )
                    }
                    onPhaseToggle={handlePhaseToggle}
                    onUpload={(files, element) =>
                      validateForm({ element, files }).then((validate) => {
                        if (!validate[element.activityId]) {
                          return handleUploadActivityFile(
                            files,
                            element,
                            errors,
                            setErrors,
                            setValues
                          );
                        }
                        props.setFieldTouched(element.activityId, true);
                      })
                    }
                    onRemove={(files, elements) =>
                      handleRemoveActivityFile(
                        files,
                        elements,
                        errors,
                        setErrors,
                        setValues
                      )
                    }
                    onSubmit={handleSubmit}
                    onKeyDown={(e, element) =>
                      handleOnKeyDown(e, element, errors, setErrors, setValues)
                    }
                    onCancel={handleCancel}
                  />

                  {values.formating !== "Phases" && (
                    <div className="activity-form__footer">
                      <Button
                        className="activity-form__cancelbtn"
                        onClick={handleCancel}
                      >
                        {values.canceltext}
                      </Button>
                      <Button disabled={!values.accepting} onClick={handleSubmit}>
                        {values.sendtext}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default ActivityForm;
